import * as Sentry from "@sentry/browser";
import useNotifications from "features/Notifications/hooks/useNotifications";
import useEnsureVideoSubtitlesBlobUrl from "hooks/api/useEnsureVideoSubtitlesBlobUrl";
import isRequestAborted from "hooks/api/utils/isRequestAborted";
import { useCallback, useEffect, useState } from "react";

function revokeBlobUrl(url: string | null) {
  if (url?.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

/** BCP 47 tag for `<track srclang>` from API `languageCode` (e.g. en_US → en-US). */
function subtitleTrackSrcLang(languageCode: string | null | undefined): string {
  if (languageCode == null || languageCode.trim() === "") {
    return "und";
  }
  return languageCode.replaceAll("_", "-");
}

interface SubtitleTrack {
  kind: "subtitles";
  src: string;
  srcLang: string;
  label: string;
  default: boolean;
}

interface SubtitleUrls {
  sourceSubtitleUrl: string | null;
  englishSubtitleUrl: string | null;
  fileTracks: SubtitleTrack[];
}

/**
 * Owns the auto-loaded subtitle blob URLs for a video: fetches them (with
 * polling) when `autoLoadSubtitles` is true, surfaces failures, and revokes the
 * blob URLs on change/unmount to avoid leaks. Also derives the ReactPlayer
 * `<track>` config from the resolved URLs and the video's language.
 */
export default function useAutoLoadedSubtitleUrls(
  videoId: string | undefined,
  autoLoadSubtitles: boolean,
  languageCode: string | null | undefined,
): SubtitleUrls {
  const { ensureSubtitlesBlobUrlsWithPoll } = useEnsureVideoSubtitlesBlobUrl();
  const { setNotification } = useNotifications();
  const [sourceSubtitleUrl, setSourceSubtitleUrl] = useState<string | null>(
    null,
  );
  const [englishSubtitleUrl, setEnglishSubtitleUrl] = useState<string | null>(
    null,
  );

  const clearSubtitleUrls = useCallback(() => {
    setSourceSubtitleUrl((prev) => {
      revokeBlobUrl(prev);
      return null;
    });
    setEnglishSubtitleUrl((prev) => {
      revokeBlobUrl(prev);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!autoLoadSubtitles || !videoId) {
      clearSubtitleUrls();
      return;
    }

    clearSubtitleUrls();
    const ac = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const { sourceUrl, englishUrl } = await ensureSubtitlesBlobUrlsWithPoll(
          videoId,
          ac.signal,
        );
        if (cancelled) {
          return;
        }
        setSourceSubtitleUrl(sourceUrl);
        setEnglishSubtitleUrl(englishUrl ?? null);
      } catch (e) {
        if (cancelled || isRequestAborted(e)) {
          return;
        }
        Sentry.captureException(e);
        setNotification({
          message:
            (e as Error)?.message ||
            "Could not load subtitles. Try again later.",
          severity: "error",
        });
        clearSubtitleUrls();
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [
    autoLoadSubtitles,
    videoId,
    ensureSubtitlesBlobUrlsWithPoll,
    setNotification,
    clearSubtitleUrls,
  ]);

  useEffect(() => {
    return () => {
      revokeBlobUrl(sourceSubtitleUrl);
      revokeBlobUrl(englishSubtitleUrl);
    };
  }, [sourceSubtitleUrl, englishSubtitleUrl]);

  const fileTracks: SubtitleTrack[] = sourceSubtitleUrl
    ? englishSubtitleUrl
      ? [
          {
            kind: "subtitles",
            src: sourceSubtitleUrl,
            srcLang: subtitleTrackSrcLang(languageCode),
            label: "Original",
            default: false,
          },
          {
            kind: "subtitles",
            src: englishSubtitleUrl,
            srcLang: "en-US",
            label: "English (US)",
            default: true,
          },
        ]
      : [
          {
            kind: "subtitles",
            src: sourceSubtitleUrl,
            srcLang: subtitleTrackSrcLang(languageCode),
            label: "Subtitles",
            default: true,
          },
        ]
    : [];

  return { sourceSubtitleUrl, englishSubtitleUrl, fileTracks };
}
